import { Table } from 'antd';
import { rightsService } from "../../../../services/rightsService";
import { forwardRef, useEffect, useState } from 'react';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import { frenchMonth } from '../../../../variables/functions';
import { exportBordereauExcel, moisPeriodeLabel } from './bordereauExcel';
import { getCompagnieLogo } from '../../../../hooks/useFilesHooks';
import BordereauPrintTable from './BordereauPrintTable';

const BordereauRecoursEncaisses = forwardRef(({
    data,
    columns,
    selectedRows,
    onSelectionChange,
    onValidate,
    onPrint,
    isValidated,
    isDisabled,
    loading,
    categorie,
    periode,
    compagnie
}, ref) => {

    const [isPrinting, setIsPrinting] = useState(false);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    // Logo de la compagnie pour l'en-tête répété (BordereauPrintTable).
    useEffect(() => {
        if (compagnie?.logo && compagnie?.code) {
            getCompagnieLogo(compagnie.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    const blob = new Blob([imageBytes], { type: 'image/png' });
                    setLogoPreviewUrl(URL.createObjectURL(blob));
                })
                .catch((e) => console.log(e));
        }
    }, [compagnie]);

    // Select all rows by default when data changes and no selection exists
    useEffect(() => {
        if (data.length > 0 && selectedRows.length === 0 && !isValidated) { // défaut = tout sélectionné, même désactivé (sinon TOTAL=0)
            onSelectionChange(data); // all rows selected initially
        }
    }, [data, selectedRows, onSelectionChange, isValidated, isDisabled]);


    // Décocher des lignes pendant une cession est SOUS HABILITATION (HLA099).
    // Sans le droit : cases désactivées → l'utilisateur cède l'intégralité, sans
    // pouvoir en retirer.
    const peutDecocher = rightsService("HLA099");

    const rowSelection = {
        selectedRowKeys: selectedRows.map(r => r.key || r.codeId),
        onChange: (selectedRowKeys, selectedRows) => {
            onSelectionChange(selectedRows);
        },
        getCheckboxProps: () => ({
            disabled: isValidated || isDisabled || !peutDecocher,
        }),
    };

    // const totalRecours = data.reduce((sum, item) => sum + (item.montant || 0), 0);
    const handlePrint = () => {
        setIsPrinting(true);
        // Petit délai pour s'assurer que le DOM est mis à jour
        setTimeout(() => {
            onPrint();
            // Remettre l'état après l'impression
            setTimeout(() => setIsPrinting(false), 100);
        }, 100);
    };

    const handleExportExcel = () => {
        const total = (data || []).reduce(
            (sum, item) => sum + (selectedRows.find((r) => (r.codeId || r.key) === (item.codeId || item.key)) ? (item.montant || 0) : 0), 0);
        exportBordereauExcel({
            titre: `BORDEREAU DES RECOURS ENCAISSES DU MOIS DE ${moisPeriodeLabel(periode).toUpperCase()}`,
            metaLines: [`Catégorie ${categorie?.codeCategorie || ''}`, `Compagnie : ${compagnie?.raisonSociale || ''}`],
            headers: ['N° Sinistre', 'N° Police', 'Assuré', 'Date survenance', 'Catégorie', 'Justificatif', 'Montant'],
            rows: (data || []).map((it) => [
                it.codeSinistre, it.codePolice, it.assure, it.dateSurvenance, it.categorie, it.justificatif,
                Math.trunc(it.montant || 0),
            ]),
            totalRow: ['TOTAL', '', '', '', '', '', Math.trunc(total)],
            nomFichier: `Bordereau_Recours_Encaisses_${categorie?.codeCategorie || ''}_${moisPeriodeLabel(periode)}`,
        });
    };

    return (
        <div>
            {/* Version affichage */}
            <div className="mb-4" style={{ padding: 15 }}>
                <Table
                    rowKey={(record) => record.key || record.codeId}
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={data}
                    pagination={{ pageSize: 10 }}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={6}>
                                    <strong>TOTAL</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                    <strong>{displayNumbers(data?.reduce(
                                        (sum, item) =>
                                            sum + ((selectedRows.find(r => r.key === item.key)) ? (item.montant || 0) : 0),
                                        0
                                    ) || 0)} F CFA</strong>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />

                <div className="mt-3 d-flex gap-2">
                    <button
                        type="button"
                        onClick={onValidate}
                        disabled={isDisabled || isValidated || loading}
                        className={isValidated ? "btn btn-success" : "btn btn-info"}
                    >
                        {loading && (
                            <span className="spinner-grow spinner-grow-sm me-1" role="status" aria-hidden="true"></span>
                        )}
                        <i className="tf-icons bx bxs-badge-check bx-18px me-2"></i>
                        <span className="align-middle">
                            {loading ? 'Validation...' : isValidated
                                ? `Recours encaissés ${categorie?.codeCategorie} de ${periode ? frenchMonth(periode?.split("-")?.[1]) + " " + periode.split("-")?.[0] : ''} validés`
                                : `Valider les recours encaissés ${categorie?.codeCategorie} de ${periode ? frenchMonth(periode.split("-")[1]) + " " + periode.split("-")[0] : ''}`
                            }
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={handlePrint}
                        className="btn btn-label-pinterest"
                    >
                        <i className="tf-icons bx bxs-file-pdf bx-18px me-2"></i>
                        Imprimer (PDF)
                    </button>

                    <button
                        type="button"
                        onClick={handleExportExcel}
                        className="btn btn-label-success"
                    >
                        <i className="tf-icons bx bxs-file-export bx-18px me-2"></i>
                        Exporter Excel
                    </button>
                </div>
            </div>

            {/* Version impression (cachée) */}
            <div ref={ref} style={{
                position: isPrinting ? 'relative' : 'absolute',
                left: isPrinting ? 'auto' : '-9999px',
                visibility: isPrinting ? 'visible' : 'hidden'
            }}>
                <style>
                    {`
                        @media print {
                            @page {
                                size: landscape;
                                margin: 20mm;
                            }
                            body {
                                print-color-adjust: exact;
                                -webkit-print-color-adjust: exact;
                            }
                        }
                    `}
                </style>
                <div style={{ padding: '10mm 8mm', fontFamily: 'Arial, sans-serif' }}>
                    <BordereauPrintTable
                        titre="BORDEREAU DES RECOURS ENCAISSES"
                        categorie={categorie}
                        compagnie={compagnie}
                        periode={periode}
                        logoPreviewUrl={logoPreviewUrl}
                        data={data}
                        rowKey="codeId"
                        columns={[
                            { title: 'N° Sinistre', get: (it) => it.codeSinistre },
                            { title: 'N° Police', get: (it) => it.codePolice },
                            { title: 'Assuré', get: (it) => it.assure },
                            { title: 'Date survenance', get: (it) => it.dateSurvenance },
                            { title: 'Catégorie', get: (it) => it.categorie },
                            { title: 'Justificatif', get: (it) => it.justificatif },
                            { title: 'Montant', align: 'right', get: (it) => displayNumbers(it.montant) },
                        ]}
                        totalValue={`${displayNumbers((data || []).reduce((sum, item) => sum + (selectedRows.find((r) => (r.codeId || r.key) === (item.codeId || item.key)) ? (item.montant || 0) : 0), 0) || 0)} F CFA`}
                    />
                </div>
            </div>
        </div>
    );
});

BordereauRecoursEncaisses.displayName = 'BordereauRecoursEncaisses';

export default BordereauRecoursEncaisses;